import './index.css'
import api from '@disco/api/server'
import {
  BUSINESS,
  EMAIL,
  RECIPIENT_GROUP,
  USER,
} from '@disco/constants/RecipientTypes'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { parseServerError } from '@disco/utils/captureError'
import { getEmailHandleFromAngularBrackets } from '@disco/utils/email'
import captureError from '@disco/utils/captureError'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import type { BusinessId, Recipient } from 'types/list'
import type { DropdownOption } from 'types/ui'
import RecipientLabel from 'components/common/email-groups/recipient-label'
import { Box } from 'components/ds'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Flex from 'components/ui/flex'
import InlineError from 'components/ui/inline-error'
import Stack from 'components/ui/stack'
import RecipientTypeaheadOption from '../recipient-typeahead-option'
import RecipientDropdown from './recipient-dropdown'
type Props = {
  value: (Record<string, any> | Record<string, any>[]) | null | undefined
  onAddRecipient: (...args: Array<any>) => any
  isRecipientInvalid: (...args: Array<any>) => any
  onOptionSelected?: (...args: Array<any>) => any
  csrfToken: string
  placeholder?: string
  autoSuggest?: boolean
  hideBusinesses?: boolean
  includeBlankEmails?: boolean
  noBusinessChoice?: boolean
  assignUrlContactsEnabled?: boolean
  isTrial?: boolean
  isPhone: boolean
}

type State = {
  recipient: Recipient
  inputValue: Recipient | null | undefined
  suggestions: DropdownOption[]
  loading: boolean
  apiError: Record<string, any> | null | undefined
}

/**
 * Add recipient component for email group management with search and validation
 * Supports email addresses, DISCO users, businesses, and recipient groups with typeahead search
 * Handles business selection for multi-business users and validation with error handling
 * @param value - Current recipient value(s)
 * @param onAddRecipient - Callback when recipients are added
 * @param isRecipientInvalid - Function to validate recipient
 * @param onOptionSelected - Callback when option is selected from dropdown
 * @param csrfToken - CSRF token for API requests
 * @param placeholder - Placeholder text for input field
 * @param autoSuggest - Whether to enable auto-suggest functionality
 * @param hideBusinesses - Whether to hide business options
 * @param includeBlankEmails - Whether to include blank email options
 * @param noBusinessChoice - Whether to disable business selection
 * @param assignUrlContactsEnabled - Whether assign URL contacts feature is enabled
 * @param isTrial - Whether user is on trial
 * @param isPhone - Whether device is a phone
 */
class AddRecipient extends React.Component<Props, State> {
  _typeahead: React.Component<any, any>
  static defaultProps = {
    placeholder: 'Email Address or DISCO User',
    autoSuggest: true,
  }
  state: State = {
    recipient: {
      email: '',
      type: EMAIL,
      name: '',
    },
    inputValue: null,
    suggestions: [],
    loading: false,
    apiError: null,
  }

  componentWillUnmount() {
    if (this._abortController) this._abortController.abort()
  }

  _abortController: AbortController | null | undefined
  _getSearchResults = (
    query: string,
    callback: (...args: Array<any>) => any,
  ) => {
    const emailHandle = getEmailHandleFromAngularBrackets(query)

    if (this.props.autoSuggest && emailHandle.email.length > 2) {
      if (this._abortController) this._abortController.abort()
      this.setState({
        apiError: null,
      })
      this._abortController = new AbortController()
      api
        .getSearchResults(
          'share',
          emailHandle.email,
          {
            includeBlankEmails: this.props.includeBlankEmails,
            hideBusinesses: this.props.hideBusinesses,
          },
          this._abortController.signal,
        )
        .then(suggestions => {
          callback(null, {
            options: suggestions.map(x => ({
              // Extend object to make it work in RecipientTag and react-select
              ...x,
              value: x.id,
              label: x.name,
            })),
          })
        })
        .catch(e => {
          const apiError = e?.body
            ? e?.body?.[0] || parseServerError(e?.body)
            : null
          this.setState({
            apiError,
          })
          captureError(e)
          callback(null, {
            options: [
              {
                value: apiError,
                error: true,
                disabled: true,
              },
            ],
          })
        })
    } else {
      callback(null, [])
    }

    this.setState({})
  }
  _newOption = (option: Record<string, any>): Record<string, any> => {
    const { label, labelKey, valueKey } = option
    const emailHandle = getEmailHandleFromAngularBrackets(option.label)
    return {
      // Extend object to make it work in RecipientTag and react-select
      [valueKey]: label,
      [labelKey]: label,
      value: emailHandle.email,
      email: emailHandle.email,
      name: emailHandle.name,
      type: EMAIL,
    }
  }
  _handleOptionSelection = (option: Recipient) => {
    this.setState({
      recipient: option,
    })
    if (this.props.onOptionSelected) this.props.onOptionSelected(option)
  }
  _getOptionComponent = (option: Recipient) => {
    return this._getOptionRepresentation(option)
  }
  _mapRecipient = (recipient: Recipient) => {
    // for recipients with one business we still had to pass their business
    const singleBusiness =
      recipient.businesses && recipient.businesses.length === 1
        ? recipient.businesses[0]
        : null
    const emptyBusinessIds =
      !recipient.business_ids || recipient.business_ids.length === 0

    if (singleBusiness && emptyBusinessIds) {
      const businessId = singleBusiness.id
      return { ...recipient, business_ids: [businessId] }
    } else {
      return recipient
    }
  }
  _handleAddRecipient = (values: Recipient[]) => {
    this.props.onAddRecipient(values.map(this._mapRecipient))
  }
  _handleRemoveRecipient = (removedItem: Recipient | DropdownOption) => {
    const currentValue = this.props.value || []
    let newSelectedValue = [...(currentValue as Recipient[])]

    if (removedItem && 'id' in removedItem) {
      newSelectedValue = (currentValue as Recipient[]).filter(
        ({ id }) => id !== removedItem.id,
      )
    } else if (removedItem && 'value' in removedItem) {
      newSelectedValue = currentValue.filter(
        ({ value }) => value !== removedItem.value,
      )
    }

    this._handleAddRecipient(newSelectedValue)
  }
  _handleAddSingleRecipient = () => {
    let { inputValue } = this.state
    if (!inputValue) return // button is disabled otherwise

    this.props.onAddRecipient(this._mapRecipient(inputValue))
    // TODO: clear inputValue only when there's no error
    this.setState({
      inputValue: null,
    })
  }

  _getOptionRepresentation(option: Recipient) {
    const { type } = option
    let { name, email } = option
    name = name ? name.trim() : ''
    email = email ? email.trim() : ''

    switch (type) {
      case USER:
        if (email && name) {
          return `${name} <${email}>`
        } else {
          return email || name || ''
        }

      case BUSINESS:
        return name

      case EMAIL:
        return email

      case RECIPIENT_GROUP:
        return name

      default:
        return name
    }
  }

  _filterTypeaheadOption(inputString: string | null | undefined) {
    if (!inputString || inputString.length < 3) return false
    return true
  }

  _toggleUserBusiness(id: BusinessId) {
    const business_ids = this.state.recipient.business_ids || []
    let newBusinessIds = []

    if (business_ids && business_ids.indexOf(id) > -1) {
      newBusinessIds = business_ids.filter(businessId => businessId !== id)
    } else {
      newBusinessIds = [...business_ids, id]
    }

    this.setState({
      recipient: { ...this.state.recipient, business_ids: newBusinessIds },
    })
  }

  _renderError(defaultError?: string) {
    const validationError = this.props.isRecipientInvalid(
      this.state.recipient,
      this.state.suggestions,
    )
    const error = this.state.apiError || defaultError || validationError

    if (error && typeof error === 'string') {
      return <InlineError error={error} />
    }

    return null
  }

  _renderUserBusinesses() {
    const chosenUser = this.state.recipient
    if (this.props.noBusinessChoice) return null
    if (!chosenUser) return null
    if (!chosenUser.businesses) return null
    const hasMultipleBusinesses = chosenUser.businesses.length > 1

    if (chosenUser && chosenUser.type === 'user' && hasMultipleBusinesses) {
      const businesses = chosenUser.businesses.map(business => {
        return (
          <label key={business.id} className="Form_Tag">
            <input
              className="Form_Tag"
              type="checkbox"
              onChange={() => this._toggleUserBusiness(business.id)}
            />
            {business.name}
          </label>
        )
      })
      return (
        <Box mb="4" className="clearfix">
          <label className="Form_Label">Businesses:</label>
          <div className="Form_Field">{businesses}</div>
        </Box>
      )
    }

    return null
  }

  _renderAlert = () => {
    return (
      <>
        <Alert>
          <span className="Form_TrialMessage">
            Want to send your playlist to a contact, but prefer to send it
            yourself, rather than use DISCO’s email share function? Use assigned
            URLs. Send however you like, and still get in depth stats.
            <br />
            <Link to="/settings/billing">Upgrade</Link>
          </span>
        </Alert>
        <br />
      </>
    )
  }

  render() {
    const { hideBusinesses, assignUrlContactsEnabled, isPhone } = this.props
    // TODO: check isRecipientInvalid
    // const isEnabled = !validationError && !this.state.loading
    const isEnabled = !!this.state.inputValue && assignUrlContactsEnabled
    // TODO: clean-up
    // const typeaheadStyles = {
    //   input: cn('Form_Input', { 'is-disabled': !isEnabled }, 'is-inline'),
    //   results: 'Form_Dropdown',
    //   listItem: 'Form_DropdownOption',
    //   hover: 'Form_DropdownOption-active',
    // }
    let hasAddButton = Boolean(this.props.hideBusinesses)
    let onInputChange = this._handleAddRecipient
    let value = this.props.value
    let multi = true

    if (hasAddButton) {
      onInputChange = values => {
        this.setState({
          // @ts-expect-error
          inputValue: values,
        })
      }

      value = this.state.inputValue
      multi = false
    }

    let assignURLButton = hasAddButton && (
      <Button
        appearance="primary"
        caption="Assign URL"
        disabled={!isEnabled}
        onClick={this._handleAddSingleRecipient}
        fullWidth={isPhone}
        textCenter={isPhone}
      />
    )

    if (assignURLButton) {
      assignURLButton = isPhone ? (
        assignURLButton
      ) : (
        <ButtonContainer>{assignURLButton}</ButtonContainer>
      )
    }

    return (
      <div>
        {!assignUrlContactsEnabled && hideBusinesses && this._renderAlert()}
        <div className="Form_Label">
          Enter an email address or search for a DISCO user or{' '}
          <span>{this.props.hideBusinesses ? 'name' : 'business'}</span>
        </div>
        <Stack space="xsmall">
          <div className="RecipientInput">
            <Flex>
              <Flex.Content>
                <RecipientDropdown
                  multi={multi}
                  value={value}
                  placeholder={this.props.placeholder}
                  options={this.state.suggestions}
                  onChange={onInputChange}
                  valueComponent={(
                    { value }, // RecipientTypeaheadOption renders only if hideBusinesses == false
                  ) => (
                    <RecipientTypeaheadOption
                      value={value}
                      onRemove={() => {
                        this._handleRemoveRecipient(value)
                      }}
                    />
                  )}
                  optionRenderer={v => {
                    if (v.error) {
                      return (
                        <div className="Recipient_ErrorOption">
                          {this._renderError(v.value)}
                        </div>
                      )
                    }

                    return <RecipientLabel appearance="twoline" recipient={v} />
                  }}
                  hideArrow
                  loadOptions={this._getSearchResults}
                  isValidNewOption={option =>
                    !this.props.isRecipientInvalid(this._newOption(option))
                  }
                  promptTextCreator={input => input}
                  newOptionCreator={this._newOption}
                  disabled={hideBusinesses && !assignUrlContactsEnabled}
                />
              </Flex.Content>
              {!isPhone && assignURLButton}
            </Flex>
          </div>
          <div>{isPhone && assignURLButton}</div>
        </Stack>
        {this._renderError()}
        {this._renderUserBusinesses()}
      </div>
    )
  }
}

export default connect(state => {
  return {
    isPhone: isDeviceTypePhone(state),
  }
})(AddRecipient) as any
